import React, { useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Grid, Typography, TextField, MenuItem, Button, Box, IconButton } from '@mui/material';
import { Theme } from '@mui/material/styles';

import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';

import useDebouncedForm from 'common/hooks/useDebouncedForm';

import ContainerBox from 'components/ContainerBox';
import * as ActiveProductActions from 'actions/ActiveProductActions';
import * as ActiveProductSelectors from 'selectors/ActiveProductSelectors';
import { useTranslation } from 'services/localization/useTranslation';
import { RiCloseLine } from 'react-icons/ri';

const InformationBlock = () => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const product = useSelector(ActiveProductSelectors.productDataWithChanges);
	const isEditingDisabled = useSelector(ActiveProductSelectors.isEditingDisabled);

	const { state, editState } = useDebouncedForm({
		memoizedInitialState: useMemo(
			() => ({
				customValuesToCollect: product?.customValuesToCollect || [],
				collectProductCode: product?.collectProductCode || false,
			}),
			[product],
		),
		memoizedOnDebounce: useCallback(
			(changes) => {
				dispatch(ActiveProductActions.editLocalProduct(changes));
			},
			[dispatch],
		),
	});

	const handleChange = (
		field: string,
		index: number,
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
	) => {
		editState({
			customValuesToCollect: state.customValuesToCollect.map((item, idx) => {
				if (idx === index) {
					return {
						...item,
						[field]: e.target.value,
					};
				}
				return item;
			}),
		});
	};

	const handleDelete = (index: number) =>
		editState({
			customValuesToCollect: state.customValuesToCollect.filter((_, idx) => idx !== index),
		});

	const handleAdd = () => {
		editState({
			customValuesToCollect: state.customValuesToCollect.concat({
				name: '',
				type: 'text',
			}),
		});
	};

	if (product?.external) return null;

	return (
		<ContainerBox>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Typography variant="h6">
						{t('product.requiredInformationTitle', 'Required product information')}
					</Typography>
					<Typography variant="body2" color="secondary" sx={{ mb: 2 }}>
						{t(
							'product.requiredInformationDescription',
							'Information regarding the product that must be filled by shop staff before starting rentals',
						)}
					</Typography>
				</Grid>
				{state.customValuesToCollect!.map((customValueItem, index) => (
					<Grid key={index} container item spacing={2} className={classes.listItem}>
						<Grid item xs={12} sm={6}>
							<TextField
								fullWidth
								variant="standard"
								label={t('common:form.name')}
								placeholder={t(
									'product.requiredInformationNameHelperText',
									'I.e. product condition when rented out',
								)}
								value={customValueItem.name}
								onChange={(e) => handleChange('name', index, e)}
								InputLabelProps={{
									shrink: true,
								}}
								margin="normal"
								type="text"
								disabled={!!isEditingDisabled}
							/>
						</Grid>
						<Grid item xs={10} sm={5}>
							<TextField
								fullWidth
								variant="standard"
								label={t('product.typeOfCollectedValue', 'The type of the collected value: ')}
								select
								value={customValueItem.type}
								onChange={(e) => handleChange('type', index, e)}
								InputLabelProps={{
									shrink: true,
								}}
								SelectProps={{
									displayEmpty: false,
								}}
								margin="normal"
								type="text"
								disabled={!!isEditingDisabled}
							>
								<MenuItem value="text">{t('common:form.text', 'Text')}</MenuItem>
								<MenuItem value="number">{t('common:form.number', 'Number')}</MenuItem>
							</TextField>
						</Grid>
						<Grid item xs={2} sm={1}>
							<Box
								display="flex"
								height="100%"
								flexDirection="row"
								alignItems="center"
								justifyContent="flex-end"
							>
								<IconButton
									onClick={() => handleDelete(index)}
									disabled={!!isEditingDisabled}
									size="large"
								>
									<RiCloseLine />
								</IconButton>
							</Box>
						</Grid>
					</Grid>
				))}
				<Grid item xs={12}>
					<Box mt={3} display="flex" flexDirection="row" flexWrap="wrap">
						<Button
							variant="outlined"
							color="primary"
							onClick={handleAdd}
							disabled={!!isEditingDisabled}
						>
							{t('product.addRequiredField', 'Add required field')}
						</Button>
					</Box>
				</Grid>
			</Grid>
		</ContainerBox>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		listItem: {
			marginTop: theme.spacing(2),
		},
	}),
);

export default InformationBlock;
